import { useState } from "react";

import { colors } from "#app/theme/v2/colors";
import { radius } from "#app/theme/v2/radius";
import { shadows } from "#app/theme/v2/shadows";
import { getSpacing, spacing } from "#app/theme/v2/spacing";
import { Icon } from "#shared/icons/Icon";

import classes from "./Switch.module.css";

type SwitchSize = "sm" | "md";

type SwitchProps = {
  checked?: boolean;
  defaultChecked?: boolean;
  disabled?: boolean;
  onChange?: (checked: boolean) => void;
  size?: SwitchSize;
  id?: string;
  name?: string;
  "aria-label"?: string;
};

const SIZE_STYLES = {
  sm: {
    width: spacing[8],
    height: spacing[5],
    thumb: spacing[4],
  },
  md: {
    width: spacing[9],
    height: spacing[6],
    thumb: spacing[5],
  },
} as const;

const Switch = ({
  checked,
  defaultChecked = false,
  disabled = false,
  onChange,
  size = "md",
  id,
  name,
  "aria-label": ariaLabel,
}: SwitchProps) => {
  const [uncontrolledChecked, setUncontrolledChecked] = useState(defaultChecked);
  const isChecked = checked ?? uncontrolledChecked;
  const sizeStyle = SIZE_STYLES[size];

  const toggle = () => {
    if (disabled) {
      return;
    }

    const nextChecked = !isChecked;
    if (checked === undefined) {
      setUncontrolledChecked(nextChecked);
    }
    onChange?.(nextChecked);
  };

  return (
    <button
      type="button"
      role="switch"
      id={id}
      name={name}
      aria-label={ariaLabel}
      aria-checked={isChecked}
      disabled={disabled}
      className={classes.track}
      onClick={toggle}
      style={
        {
          "--switch-width": sizeStyle.width,
          "--switch-height": sizeStyle.height,
          "--switch-thumb-size": sizeStyle.thumb,
          "--switch-radius": radius.md,
          "--switch-inset": spacing[0.5],
          "--switch-travel": spacing[3],
          "--switch-outline-width": spacing[0.5],
          "--switch-outline-offset": spacing[0.5],
          "--switch-off": colors.bg.button.switch.default,
          "--switch-off-hover": colors.bg.button.switch.hover,
          "--switch-off-pressed": colors.bg.button.switch.pressed,
          "--switch-on": colors.bg.button.switch.activeDefault,
          "--switch-on-hover": colors.bg.button.switch.selectedHover,
          "--switch-on-pressed": colors.bg.button.switch.selectedHover,
          "--switch-disabled": colors.bg.button.switch.disabled,
          "--switch-thumb": colors.bg.elevation.primary,
          "--switch-shadow": disabled ? "none" : shadows.elevation2,
          "--switch-check": disabled ? colors.icon.primaryDisabled : colors.icon.primaryDefault,
        } as React.CSSProperties
      }
    >
      <span className={classes.thumb}>
        {size === "md" && isChecked ? <Icon name="check" size={getSpacing(3)} /> : null}
      </span>
    </button>
  );
};

export { Switch };
